<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading && !isFileLicensed"
        class="font-medium text-green-900 cursor-pointer"
        :class="{ 'opacity-40 pointer-events-none': isBasicUser }"
        @click="handleSave"
      >
        Save
      </div>
      <Icon
        v-else-if="loading"
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    <div>Privacy controls</div>
    <Pill v-if="isBasicUser" class="ml-2.5" success>Pro only</Pill>
  </DialogHeader>
  <DialogContent>
    <UpgradeToProCallout v-if="isBasicUser" class="mb-5">
      Enable privacy/invite controls and other features
    </UpgradeToProCallout>

    <div :class="classes">
      <!-- Private -->
      <div class="flex justify-between">
        <div class="font-primary text-gray-800">
          <div
            class="flex items-center font-semibold font-primary text-[14px] leading-[18px] mb-1"
          >
            <Icon :size="20" name="Lock" class="mr-2 stroke-gray-800" />
            <div>Make file private</div>
          </div>
          <div class="max-w-[430px]">
            Only you and those you invite can see this file and its description
          </div>
        </div>
        <Toggle v-model="isPrivate" :disabled="loading" />
      </div>
    </div>
  </DialogContent>
  <DialogFooter v-if="!isFileLicensed">
    <UiButton class="mr-2" :disabled="loading" @click="close()">
      Cancel
    </UiButton>
    <UiButton
      primary
      :disabled="isBasicUser"
      :loading="loading"
      @click="handleSave"
    >
      Save
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, Toggle, UiButton, Pill } from '@dacr/ui'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import {
  UserPermission,
  useUpdateFileSettingsMutation,
} from '@/shared/graphql/legacy/generated'
import type { File } from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'
import { updateFileInExplorerCache } from '@/features/explorer/utils/explorerCache'
import { computed, ref } from 'vue'

const { close, triggerEvent } = useDialog()

const props = defineProps<{
  file: File
}>()

const userStore = useUserStore()

const { mutate: updateFileSettings, loading } = useUpdateFileSettingsMutation()

const file = ref({ ...props.file })
const isPrivate = ref(file.value.settings?.isPrivate ?? false)

const isFileLicensed = false

const isBasicUser = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const classes = computed(() => ({
  'opacity-30 pointer-events-none': isBasicUser.value,
  'mt-4': !isBasicUser.value,
}))

const handleSave = async () => {
  await updateFileSettings({
    input: {
      id: file.value.id,
      name: file.value.name,
      description: file.value.description ?? '',
      isPrivate: isPrivate.value,
    },
  })

  updateFileInExplorerCache(file.value.id, {
    settings: { __typename: 'FileSettings', isPrivate: isPrivate.value },
  })

  triggerEvent('success', {
    ...file.value,
    settings: { ...file.value.settings, isPrivate: isPrivate.value },
  })

  close()
}
</script>
